import { useLazyQuery, useQuery } from '@apollo/client';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  GET_ASSET_VALUE,
  GET_PROPERTY_ESTIMATION,
} from '../../../http/apollo-queries';
import { DatePicker, InputDropdown } from '../../form';
import { GetAssetValueVars, PropertyAsset } from '../types';
import { Debug } from '../../Debug/Debug';
import PropertyInfo from './PropertyInfo';
import { PropertyAssetFormProps } from './types';
import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { NumberFormatValues } from 'react-number-format';
import { AssetPrice } from '../FinancialAsset';
import { CurrencyCodeEnum } from '../../../constants/enums';
import { formatAWSDate } from '../../../helpers/functions';
import { logger } from '../../../helpers/logger';
import { numberFormat } from '../../../helpers/calc';

const PropertyAssetForm: React.FC<PropertyAssetFormProps> = ({
  formik,
  reportingCurrency,
}) => {
  const { t } = useTranslation();

  // DATA
  const [addressSuggestions, setAddressSuggestions] = useState<
    PropertyAsset[] | null
  >(null);

  const [selectedAddress, setSelectedAddress] = useState<PropertyAsset | null>(
    null
  );

  // QUERIES
  const [getPropertyEstimation, { loading: loadingEstimation }] = useLazyQuery<{
    getPropertyEstimation: PropertyAsset[];
  }>(GET_PROPERTY_ESTIMATION);

  const { loading: loadingExchangePrice, refetch: getExchangePrice } = useQuery<
    AssetPrice,
    GetAssetValueVars
  >(GET_ASSET_VALUE, {
    variables: {
      assetId: 'USD/EUR',
      date: formik.values.purchasedAt || formatAWSDate(new Date()),
    },
    skip: reportingCurrency === CurrencyCodeEnum.USD,
    onCompleted: (data) => {
      formik.setFieldValue('entryExchangePrice', data?.getAssetValue?.eur || 1);
    },
    onError: (error) => {
      logger.error('PropertyAssetForm:getExchangePrice', error);
      formik.setFieldValue('entryExchangePrice', 1);
    },
  });

  const getAddressSuggestions = useCallback(
    debounce((address: string) => {
      // If user has selected address, don't fetch data for that specific one again
      if (address === selectedAddress?.address) return;
      if (!address) {
        setAddressSuggestions(null);
        return;
      }
      getPropertyEstimation({
        variables: {
          address: address,
        },
      })
        .then(({ data }) => {
          setAddressSuggestions(data?.getPropertyEstimation || []);
        })
        .catch((e) => {
          toast.error(e.message);
        });
    }, 200),
    [selectedAddress]
  );

  // HANDLERS
  const handleEntryPriceInAssetCurrencyChange = ({
    floatValue,
  }: NumberFormatValues) => {
    formik.setFieldValue('entryPriceInAssetCurrency', floatValue);
    formik.setFieldValue(
      'entryPriceInReportingCurrency',
      floatValue ? formik.values.entryExchangePrice * floatValue : 0
    );
  };

  const handlePurchasedAtChange = (date: Date) => {
    formik.setFieldValue('purchasedAt', formatAWSDate(date));
    if (reportingCurrency !== CurrencyCodeEnum.USD) {
      getExchangePrice({ assetId: 'USD/EUR', date: formatAWSDate(date) }).then(
        ({ data }) => {
          logger.log('PropertyAssetForm:handlePurchasedAtChange', data);
          const entryExchangePrice = data?.getAssetValue.eur || 1;
          formik.setFieldValue('entryExchangePrice', entryExchangePrice);
          formik.setFieldValue(
            'entryPriceInReportingCurrency',
            formik.values.entryPriceInAssetCurrency
              ? formik.values.entryPriceInAssetCurrency * entryExchangePrice
              : 0
          );
        }
      );
    }
  };

  // EFFECTS
  // Reset form data on load
  useEffect(
    () => () => {
      formik.setValues({
        ...formik.values,
        propertyEstimationId: '',
        purchasedAt: formatAWSDate(new Date()),
        entryPriceInReportingCurrency: undefined,
        entryPriceInAssetCurrency: undefined,
        currentPriceInAssetCurrency: undefined,
        currentPriceInReportingCurrency: undefined,
      });
    },
    []
  );

  return (
    <div className="flex flex-col items-center mx-auto w-full h-full overflow-y-auto">
      <div className={'w-3/4'}>
        <p className="text-grey-50 text-xs text-center">
          {t('strategies.addProperty.subText')}
        </p>
        <div className="mt-16 w-full">
          <InputDropdown
            placeholder={t(
              'strategies.addProperty.form.placeholder.propertyAddress'
            )}
            label={t('strategies.addProperty.form.label.propertyAddress')}
            onChange={getAddressSuggestions}
            selected={selectedAddress?.address || ''}
            loading={loadingEstimation}
          >
            {addressSuggestions !== null ? (
              addressSuggestions.length > 0 ? (
                addressSuggestions?.map((address) => {
                  return (
                    <div
                      key={address.id}
                      className="p-4 cursor-pointer"
                      onClick={() => {
                        setSelectedAddress(address);
                        // Save address id for next step
                        formik.setFieldValue(
                          'propertyEstimationId',
                          address.id
                        );
                      }}
                    >
                      {address.address}
                    </div>
                  );
                })
              ) : (
                <div className={'p-4'}>
                  {t('strategies.addProperty.noData')}
                  <Debug>
                    <div
                      key={'666'}
                      className="p-4 cursor-pointer"
                      onClick={() => {
                        setSelectedAddress({
                          id: '666fake',
                          city: 'fakeCity',
                          state: 'fakeState',
                          streetName: 'fakeStreet',
                          houseNumber: '666',
                          address: '666th FakeAvenue',
                          zestimate: '100000000',
                          zillowUrl: 'fakeUrl',
                        });
                        formik.setFieldValue('propertyEstimationId', '666');
                      }}
                    >
                      {'666th FakeAvenue'}
                    </div>
                  </Debug>
                </div>
              )
            ) : null}
          </InputDropdown>
        </div>
        {selectedAddress && (
          <>
            <PropertyInfo addressData={selectedAddress} />
            <div className={'grid grid-cols-2 gap-8 pt-2'}>
              {/* PRICE */}
              <div>
                <CustomNumber
                  className="w-full"
                  decimalScaleOnFocus={0}
                  readOnly={false}
                  onValueChange={handleEntryPriceInAssetCurrencyChange}
                  showLabel={true}
                  fixedLabel={true}
                  label={t(
                    'strategies.addProperty.form.label.entryPriceInAssetCurrency'
                  )}
                  suffix={CurrencyCodeEnum.USD}
                  suffixOverlay={true}
                  min={0}
                  value={formik.values.entryPriceInAssetCurrency}
                  placeholder={t(
                    'strategies.addProperty.form.placeHolder.entryPriceInAssetCurrency'
                  )}
                  validate={(value) => !!value && value > 0}
                  loading={loadingExchangePrice}
                />
                {formik.values.entryPriceInReportingCurrency !== undefined && (
                  <div className={'text-xs text-grey-50'}>
                    {t('strategies.configureAsset.inReportingCurrency', {
                      value: numberFormat(
                        formik.values.entryPriceInReportingCurrency
                      ),
                      currency: reportingCurrency,
                    })}
                  </div>
                )}
              </div>

              {/* DATE */}
              <div className="flex">
                <DatePicker
                  size="full"
                  placeholder={t(
                    'strategies.addProperty.form.placeholder.date'
                  )}
                  date={
                    formik.values.purchasedAt
                      ? new Date(formik.values.purchasedAt)
                      : undefined
                  }
                  id={'date'}
                  showLabel={true}
                  fixedLabel={false}
                  label={t('strategies.addProperty.form.label.date')}
                  maxDate={new Date()}
                  onChange={(date) => handlePurchasedAtChange(date as Date)}
                  validate={(value) => !!value}
                />
              </div>
            </div>

            <p className="mt-10 text-xs text-grey-70 self-start mb-4">
              &copy; Zillow, Inc., 2006-2020. Use is subject to Terms of Use
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(PropertyAssetForm);
